/* NutritionDilFoodOptimizerCard — Task 3 (iter942)
 *
 * Обединена пациент-facing карта:
 *   Nutrition targets  →  DIL budget  →  Food swap suggestions
 *
 * Ляво: макро цели + DIL таван (от /api/dil/profile).
 * Център: колко DIL е използван днес (/api/dil/today).
 * Дясно: Топ 5 храни с нисък DIL (/api/dil/suggestions) като swap предложения.
 *
 * Целта: пациентът да види КАКВО да яде за същите макроси, но с
 * по-нисък инсулинов товар. Действа като „single glance food optimizer".
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Target, Flame, Utensils, TrendingDown, Wheat, Beef, Leaf,
  Loader2, Lock, ArrowRight,
} from "lucide-react";

const fmt = (v, digits = 1) =>
  v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(digits);

export default function NutritionDilFoodOptimizerCard() {
  const { API, user } = useAuth();
  const { isDark } = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [premium, setPremium] = useState(true);
  const [dilProfile, setDilProfile] = useState(null);
  const [today, setToday] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [profRes, todayRes, sugRes] = await Promise.allSettled([
          axios.get(`${API}/dil/profile`),
          axios.get(`${API}/dil/today`),
          axios.get(`${API}/dil/suggestions`),
        ]);
        if (!alive) return;
        if (profRes.status === "fulfilled") setDilProfile(profRes.value.data);
        else setError(profRes.reason?.response?.data?.detail || "Профилът не е пълен.");
        if (todayRes.status === "fulfilled") setToday(todayRes.value.data);
        if (sugRes.status === "fulfilled") {
          setSuggestions(sugRes.value.data?.suggestions || []);
          setPremium(true);
        } else if (sugRes.reason?.response?.status === 403) {
          setPremium(false);
          setSuggestions([]);
        }
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || "Грешка при зареждане.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    if (user) load();
    return () => { alive = false; };
  }, [API, user]);

  const dil = dilProfile?.dil || {};
  const dilBudget = dil.personalized_dil;
  const carbTarget = dil.carb_target_g;
  const proteinTarget = dil.protein_target_g;
  const calorieTarget = dil.final_calories;

  const dilUsed = today?.total_dil ?? 0;
  const dilRemaining = dilBudget != null ? Math.max(0, dilBudget - dilUsed) : null;
  const pctUsed = dilBudget ? Math.min(100, (dilUsed / dilBudget) * 100) : 0;

  // Rank suggestions by best DIL/protein savings for the same macro contribution
  const topSwaps = (suggestions || [])
    .filter((s) => (s?.dil_50g != null) && ((s?.protein_100g || 0) > 0 || (s?.fiber_100g || 0) > 2))
    .sort((a, b) => (a.dil_50g || 0) - (b.dil_50g || 0))
    .slice(0, 5);

  const cardCls = isDark
    ? "bg-gradient-to-br from-slate-900 to-slate-800 border-slate-700"
    : "bg-gradient-to-br from-white to-emerald-50/40 border-emerald-100";

  return (
    <Card className={`${cardCls} border shadow-sm`} data-testid="nutrition-dil-food-optimizer">
      <CardContent className="p-4 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Utensils className={`w-4 h-4 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
            <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              Nutrition → DIL → Food Optimizer
            </h3>
          </div>
          <Badge className="bg-emerald-500/10 text-emerald-600 border border-emerald-500/30 text-[10px]">
            Единен поток
          </Badge>
        </div>

        {loading && (
          <div className="flex items-center gap-2 py-6 justify-center text-xs text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Зареждам целите ти…
          </div>
        )}

        {!loading && error && (
          <div className={`p-3 rounded-lg text-xs ${isDark ? "bg-red-500/10 text-red-300" : "bg-red-50 text-red-700"}`}
               data-testid="nutrition-dil-error">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Flow: 3 колони */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* 1. NUTRITION targets */}
              <div className={`rounded-xl p-3 ${isDark ? "bg-slate-800/60" : "bg-white/70"} border ${isDark ? "border-slate-700" : "border-slate-200"}`}
                   data-testid="nutrition-targets-panel">
                <div className="flex items-center gap-1.5 mb-2">
                  <Target className={`w-3.5 h-3.5 ${isDark ? "text-blue-400" : "text-blue-600"}`} />
                  <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-blue-300" : "text-blue-700"}`}>
                    1. Дневни цели
                  </p>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className={`flex items-center gap-1 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                      <Wheat className="w-3 h-3" /> Въглехидрати
                    </span>
                    <span className={`font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                      {fmt(carbTarget, 0)} g
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className={`flex items-center gap-1 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                      <Beef className="w-3 h-3" /> Белтък
                    </span>
                    <span className={`font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                      {fmt(proteinTarget, 0)} g
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className={`flex items-center gap-1 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                      <Flame className="w-3 h-3" /> Калории
                    </span>
                    <span className={`font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                      {fmt(calorieTarget, 0)} kcal
                    </span>
                  </div>
                </div>
              </div>

              {/* 2. DIL budget usage */}
              <div className={`rounded-xl p-3 relative ${isDark ? "bg-slate-800/60" : "bg-white/70"} border ${isDark ? "border-slate-700" : "border-slate-200"}`}
                   data-testid="dil-budget-panel">
                <div className="flex items-center gap-1.5 mb-2">
                  <ArrowRight className="w-3 h-3 text-emerald-500 hidden md:inline-block absolute -left-4 top-1/2 -translate-y-1/2" />
                  <Flame className={`w-3.5 h-3.5 ${isDark ? "text-orange-400" : "text-orange-600"}`} />
                  <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-orange-300" : "text-orange-700"}`}>
                    2. DIL бюджет днес
                  </p>
                </div>
                <div className={`text-lg font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {fmt(dilUsed, 1)} <span className="text-xs font-normal text-slate-400">/ {fmt(dilBudget, 0)} g</span>
                </div>
                <div className={`h-2 rounded-full mt-2 overflow-hidden ${isDark ? "bg-slate-700" : "bg-slate-100"}`}>
                  <div
                    className={`h-full transition-all ${
                      pctUsed < 70 ? "bg-emerald-500" : pctUsed < 90 ? "bg-yellow-500" : "bg-rose-500"
                    }`}
                    style={{ width: `${pctUsed}%` }}
                    data-testid="dil-budget-progress"
                  />
                </div>
                <p className={`text-[11px] mt-1.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  Оставащ товар: <span className="font-bold">{fmt(dilRemaining, 1)} g</span>
                </p>
              </div>

              {/* 3. FOOD OPTIMIZER (swap suggestions) */}
              <div className={`rounded-xl p-3 relative ${isDark ? "bg-slate-800/60" : "bg-white/70"} border ${isDark ? "border-slate-700" : "border-slate-200"}`}
                   data-testid="food-optimizer-panel">
                <div className="flex items-center gap-1.5 mb-2">
                  <ArrowRight className="w-3 h-3 text-emerald-500 hidden md:inline-block absolute -left-4 top-1/2 -translate-y-1/2" />
                  <TrendingDown className={`w-3.5 h-3.5 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
                  <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                    3. Замени за по-нисък DIL
                  </p>
                </div>
                {!premium ? (
                  <div className={`p-2 rounded-lg text-[11px] flex items-start gap-2 ${isDark ? "bg-purple-500/10 text-purple-200" : "bg-purple-50 text-purple-700"}`}>
                    <Lock className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>Meal Intelligence е Premium — активирай абонамент за swap препоръки.</span>
                  </div>
                ) : topSwaps.length === 0 ? (
                  <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    Няма swap предложения — бюджетът е попълнен или профилът е непълен.
                  </p>
                ) : (
                  <ul className="space-y-1" data-testid="food-optimizer-list">
                    {topSwaps.map((s, i) => (
                      <li
                        key={s.food + i}
                        className={`text-[11px] flex items-center justify-between rounded-md px-2 py-1 ${isDark ? "bg-slate-700/40" : "bg-emerald-50/60"}`}
                        data-testid={`food-swap-item-${i}`}
                      >
                        <span className={`truncate flex items-center gap-1 ${isDark ? "text-white" : "text-slate-800"}`}>
                          <Leaf className="w-2.5 h-2.5 text-emerald-500 shrink-0" />
                          {s.food}
                        </span>
                        <span className={`font-bold shrink-0 ml-2 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                          {fmt(s.dil_50g, 1)}g DIL
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Footer note */}
            <p className={`text-[10px] ${isDark ? "text-slate-500" : "text-slate-500"} pt-1`}>
              Замените запазват макросите (белтък + фибри), но намаляват инсулиновия
              товар. Дневният DIL таван вече е персонализиран (пол, цикъл,
              естроген, HOMA-IR).
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
